import type {Offer, Overlay} from '@/modules/hotels/components/MapView/models';
import type {Price} from '@/redux/v1/models';

import {FLOAT_PANE, OverlayViewF} from '@react-google-maps/api';
import {debounce} from 'lodash';
import {memo, useMemo} from 'react';
import {Link} from 'react-router';

import {HotelTile} from '@/components/HotelTile';
import {HotelTilePlaceholder} from '@/components/HotelTilePlaceholder';
import {useHotelDetails, useLocalSplit} from '@/modules/common/hooks';
import {useEnvironment} from '@/modules/common/hooks/useEnvironment';
import {useLinkBuilder} from '@/modules/common/hooks/useLinkBuilder';
import {getRatingData} from '@/modules/hotels/utils/getRatingData';
import {asComponent} from '@/utils/asComponent';

type Props = {
  id: string;
  latitude: number;
  longitude: number;
  offer: Offer;
  totalDiscountOriginal?: Price;
  onLoad: (overlay: Overlay) => void;
  onClick?: () => void;
};

const HotelTileAsLink = asComponent(HotelTile, Link);

export const HotelTileInfoWindow = memo(function HotelInfoWindow({
  id,
  latitude,
  longitude,
  offer,
  onLoad,
  onClick,
}: Props) {
  const {isDesktop} = useEnvironment();
  const {buildOfferListLink} = useLinkBuilder();

  const offerListLink = buildOfferListLink({
    hotelId: id,
    getSortParams: () => null,
  });
  const [data, {hotelDetails: hotelDetailsStatus}] = useHotelDetails(id);

  // memoized to avoid recreating the overlay too often
  // https://github.com/JustFly1984/react-google-maps-api/blob/0bd9291dfefb5fe57cd98fdeb7331532fe88410f/packages/react-google-maps-api/src/components/dom/OverlayView.tsx#L118
  const position = useMemo(
    () => ({
      lat: latitude,
      lng: longitude,
    }),
    [latitude, longitude]
  );
  // onLoad fires before layout, so we need to wait a bit before we can read
  const onLoadDebounced = useMemo(() => debounce(onLoad, 75), [onLoad]);

  const priceCacheEnabled =
    useLocalSplit('FBO_2956_price_cache').treatment === 'on';

  const {
    totalPrice,
    totalPriceInternal,
    totalPriceOriginal,
    totalDiscountDisplay,
    totalDiscountHint,
    pricePerPerson,
    discountType,
    discountPercentage,
    totalDiscountOriginal,
  } = offer;

  const rating = getRatingData({
    // @ts-expect-error ...
    hotelOffer: offer,
  });

  return (
    <OverlayViewF
      position={position}
      mapPaneName={FLOAT_PANE}
      // Default placement is top-left (from the elements pov) in relation to the `position`.
      getPixelPositionOffset={(width) => ({
        x: -(width / 2), // centered
        y: 8,
      })}
      // @ts-expect-error ...
      onLoad={(overlay: Overlay) => {
        // https://github.com/tomchentw/react-google-maps/issues/84#issuecomment-411367993
        window.google.maps.OverlayView.preventMapHitsAndGesturesFrom(
          overlay.container
        );
        onLoadDebounced(overlay);
      }}
    >
      {hotelDetailsStatus !== 'success' || !data ? (
        <HotelTilePlaceholder />
      ) : (
        <HotelTileAsLink
          to={offerListLink.to}
          state={offerListLink.options.state}
          target={isDesktop ? '_blank' : '_self'}
          // ui props
          imageUrl={data.images?.[0]?.small?.url ?? ''}
          small={true}
          name={data.name}
          category={data.category}
          categoryType={data.categoryType}
          ratingScore={rating?.score}
          totalPrice={totalPrice}
          totalPriceInternal={totalPriceInternal}
          totalPriceOriginal={totalPriceOriginal}
          totalDiscount={totalDiscountDisplay}
          pricePerPerson={pricePerPerson}
          discountType={discountType}
          discountPercentage={discountPercentage}
          totalDiscountHint={totalDiscountHint}
          totalDiscountOriginal={totalDiscountOriginal}
          canShowPriceCache={priceCacheEnabled}
          onClickHandleTracking={onClick}
        />
      )}
    </OverlayViewF>
  );
});
